import React, { useState } from "react";
import { X, AlertCircle } from "lucide-react";
import { useAuth } from "@/lib/authContext";

export default function ChangePasswordModal({ open, forced = false, onClose }) {
  const { changePassword } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) return setError("New password must be at least 8 characters.");
    if (next !== confirm) return setError("New passwords do not match.");
    setSubmitting(true);
    const res = await changePassword(current, next);
    setSubmitting(false);
    if (!res.success) return setError(res.error || "Password change failed.");
    setCurrent(""); setNext(""); setConfirm("");
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-black/70 backdrop-blur-sm" data-testid="change-password-modal">
      <div className="relative w-full max-w-md ycn-glass rounded-3xl p-8 border border-white/10">
        {!forced && (
          <button onClick={onClose} data-testid="cp-close" className="absolute top-4 right-4 h-9 w-9 rounded-full hover:bg-white/10 flex items-center justify-center text-white/60">
            <X size={16} />
          </button>
        )}
        <div className="text-[10px] uppercase tracking-[0.35em] text-white/60 mb-3">
          {forced ? "First-time login" : "Account"}
        </div>
        <h2 className="font-display text-2xl font-black">
          {forced ? "Set a new password" : "Change password"}
        </h2>
        {forced && (
          <p className="mt-1 text-sm text-white/60">You&apos;re using the default admin password. Please set a new one before continuing.</p>
        )}
        <form onSubmit={submit} className="mt-6 space-y-3">
          <input type="password" required placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="cp-current"
            className="w-full rounded-full bg-white/5 border border-white/15 focus:border-white/40 focus:bg-white/10 outline-none px-5 py-3 text-white placeholder:text-white/40" />
          <input type="password" required placeholder="New password (min 8 chars)" value={next} onChange={(e) => setNext(e.target.value)} data-testid="cp-new"
            className="w-full rounded-full bg-white/5 border border-white/15 focus:border-white/40 focus:bg-white/10 outline-none px-5 py-3 text-white placeholder:text-white/40" />
          <input type="password" required placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="cp-confirm"
            className="w-full rounded-full bg-white/5 border border-white/15 focus:border-white/40 focus:bg-white/10 outline-none px-5 py-3 text-white placeholder:text-white/40" />
          {error && (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 flex items-start gap-2 text-sm text-red-200">
              <AlertCircle size={16} className="shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}
          <button type="submit" disabled={submitting} data-testid="cp-submit"
            className="ycn-btn-primary w-full py-3 rounded-full font-bold text-white disabled:opacity-60 transition-all">
            {submitting ? "Saving…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}
